'use client';

import { triggerLandingDeploy } from '@/actions/landings-proxy/landings-proxy';
import { TenantDeploy } from '@/services/tenant-deploys';
import { ALL_DEPLOYS, normalizeDeploySelection } from '@/utils/publish';
import { Button, ReactSelectOption, SelectInput, toast } from '@payloadcms/ui';
import { useState } from 'react';

import { addPublishEntry, isLastPublishRecent } from '../../actions/publish';

const getOptionValues = (value: ReactSelectOption | ReactSelectOption[]): string[] =>
  (Array.isArray(value) ? value : [value]).flatMap((option) => (typeof option?.value === 'string' ? [option.value] : []));

export const PublishButtonClient = ({
  tenantName,
  tenantDisplayName,
  tenantId,
  deploys,
}: {
  tenantName: string;
  tenantDisplayName: string;
  tenantId: string;
  deploys: TenantDeploy[];
}) => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const deployOptions = [
    { label: 'All deploy configurations', value: ALL_DEPLOYS },
    ...deploys.map((deploy) => ({ label: deploy.bucket, value: deploy.id })),
  ];
  const selectedDeployIds = selectedValues.filter((value) => value !== ALL_DEPLOYS);
  const selectedBuckets = deploys.filter((deploy) => selectedDeployIds.includes(deploy.id)).map((deploy) => deploy.bucket);
  const publishedBuckets = selectedBuckets.length > 0 ? selectedBuckets : deploys.map((deploy) => deploy.bucket);

  const publish = async () => {
    const isRecent = await isLastPublishRecent(tenantId, publishedBuckets);

    if (isRecent) {
      toast.error(
        `${tenantDisplayName} started publishing some of these deploy configurations less than 1 minute ago. Please wait for the previous publish to finish before publishing again.`,
      );
      return;
    }

    const response = await triggerLandingDeploy(tenantName, selectedDeployIds);

    if (!response.ok) {
      toast.error(`${tenantDisplayName} failed to deploy.`);
      return;
    }

    const deployedTarget = selectedBuckets.length > 0 ? selectedBuckets.join(', ') : 'all deploy configurations';

    try {
      await addPublishEntry(tenantId, publishedBuckets);
    } catch {
      toast.warning(`"${tenantDisplayName}" is being deployed to ${deployedTarget}, but this publish could not be added to the log.`);
      return;
    }

    toast.success(
      `"${tenantDisplayName}" is being deployed to ${deployedTarget}. You will be notified through slack once the deployment is complete.`,
    );
  };

  const handlePublishButtonClick = async () => {
    setIsPublishing(true);

    try {
      await publish();
    } catch {
      toast.error(`${tenantDisplayName} could not be published. Reload the page and try again.`);
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="publish-deploys" style={{ marginTop: '20px' }}>
      <SelectInput
        path="deploys"
        name="deploys"
        hasMany
        options={deployOptions}
        value={selectedValues}
        placeholder="Select the deploy configurations to publish"
        readOnly={isPublishing}
        description='Choose "All deploy configurations", or only the ones to publish. Content shared by several deploy configurations only updates on the ones you publish.'
        onChange={(value) => setSelectedValues(normalizeDeploySelection(getOptionValues(value)))}
      />
      {selectedValues.length > 0 && (
        <Button
          onClick={() => {
            handlePublishButtonClick();
          }}
          disabled={isPublishing}
        >
          {isPublishing ? 'Publishing' : 'Publish'}
        </Button>
      )}
    </div>
  );
};

export default PublishButtonClient;
